/**
 * RISKEE Shared UI Components
 * Reusable UI components used across pages
 */
import React from 'react';
import { STATUS_CONFIG, SEVERITY_CONFIG, REPORT_TYPES, ACTION_STATUS_CONFIG } from '../../lib/constants';

// Status Badge
export const StatusBadge = ({ status, size = 'sm' }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span 
      className={`status-badge status-badge-${size}`}
      style={{ color: config.color, backgroundColor: config.bg }}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </span>
  );
};

// Severity Badge
export const SeverityBadge = ({ severity }) => {
  if (!severity) return <span className="severity-badge severity-unrated">Not assessed</span>;
  const config = SEVERITY_CONFIG[severity] || { label: severity, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span 
      className="severity-badge"
      style={{ color: config.color, backgroundColor: config.bg }}
      data-testid={`severity-badge-${severity}`}
    >
      {config.label}
    </span>
  );
};

// Report Type Badge
export const TypeBadge = ({ type }) => {
  const config = REPORT_TYPES.find(t => t.id === type) || { label: type, icon: null, emoji: '📄', color: '#6b7280' };
  return (
    <span className="type-badge" style={{ borderColor: config.color }}>
      {config.icon ? (
        <img src={config.icon} alt="" className="type-icon-img" style={{ width: '16px', height: '16px', marginRight: '4px' }} />
      ) : (
        <span className="type-icon">{config.emoji || '📄'}</span>
      )}
      <span style={{ color: config.color }}>{config.label}</span>
    </span>
  );
};

// Icon component for safety icons
export const SafetyIcon = ({ config, size = 20 }) => {
  if (config?.icon) {
    return <img src={config.icon} alt="" style={{ width: size, height: size }} />;
  }
  return <span style={{ fontSize: size }}>{config?.emoji || '📄'}</span>;
};

// Action Status Badge
export const ActionStatusBadge = ({ status }) => {
  const config = ACTION_STATUS_CONFIG[status] || { label: status, color: '#6b7280' };
  return (
    <span className="action-status-badge" style={{ color: config.color, borderColor: config.color }}>
      {config.label}
    </span>
  );
};

// Loading Spinner
export const Spinner = () => <div className="spinner" data-testid="spinner"></div>;

// Empty State
export const EmptyState = ({ icon, title, description, action }) => (
  <div className="empty-state" data-testid="empty-state">
    <span className="empty-icon">{icon}</span>
    <h3>{title}</h3>
    <p>{description}</p>
    {action}
  </div>
);

// Card Component
export const Card = ({ children, className = '', onClick, ...props }) => (
  <div className={`card ${className} ${onClick ? 'card-clickable' : ''}`} onClick={onClick} {...props}>
    {children}
  </div>
);

// Button Component (custom, not shadcn)
export const CustomButton = ({ children, variant = 'primary', size = 'md', icon, disabled, loading, ...props }) => (
  <button 
    className={`btn btn-${variant} btn-${size}`} 
    disabled={disabled || loading}
    {...props}
  >
    {loading ? <Spinner /> : icon && <span className="btn-icon">{icon}</span>}
    {children}
  </button>
);

// Input Component
export const Input = ({ label, error, ...props }) => (
  <div className="input-group">
    {label && <label className="input-label">{label}</label>}
    <input className={`input ${error ? 'input-error' : ''}`} {...props} />
    {error && <span className="input-error-text">{error}</span>}
  </div>
);

// Textarea Component
export const Textarea = ({ label, error, ...props }) => (
  <div className="input-group">
    {label && <label className="input-label">{label}</label>}
    <textarea className={`textarea ${error ? 'input-error' : ''}`} {...props} />
    {error && <span className="input-error-text">{error}</span>}
  </div>
);

// Select Component
export const Select = ({ label, options, error, ...props }) => (
  <div className="input-group">
    {label && <label className="input-label">{label}</label>}
    <select className={`select ${error ? 'input-error' : ''}`} {...props}>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {error && <span className="input-error-text">{error}</span>}
  </div>
);

// Modal Component
export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="modal-overlay">
      <div className={`modal modal-${size}`} onClick={e => e.stopPropagation()} data-testid="modal">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};
